import { flip } from "@floating-ui/dom";
import {
  FloatingFocusManager,
  FloatingList,
  FloatingPortal,
  autoUpdate,
  offset,
  size,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useInteractions,
  useListNavigation,
} from "@floating-ui/react";
import { isEmpty } from "lodash";
import {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Box } from "@design-system/Box/Box";
import { Card } from "@design-system/Card/Card";
import { Icon } from "@design-system/Icon/Icon";
import { Input } from "@design-system/Input/Input";
import { InputBox } from "@design-system/InputBox/InputBox";
import { ScrollBox } from "@design-system/ScrollBox/ScrollBox";
import { SelectContext } from "@design-system/Select/SelectContext";
import { Option } from "@design-system/Select/UI/options/Option/Option";
import { OptionContainer } from "@design-system/Select/UI/options/OptionContainer/OptionContainer";
import { Value } from "@design-system/Select/UI/values/Value/Value";
import { Spinner } from "@design-system/Spinner/Spinner";
import { Text } from "@design-system/Text/Text";

import classes from "./InfiniteSelectBase.module.css";

export type SelectOption = {
  id: string;
  label: string;
};

export interface InfiniteSelectDataHook {
  options: SelectOption[];
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
}

type Props = {
  value: string | undefined;
  onChange: (value: string | undefined, option?: SelectOption | null) => void;
  placeholder?: string;
  useData: (searchQuery: string, isOpen: boolean) => InfiniteSelectDataHook;
  loadingText?: string;
  noResultsText?: string;
  loadingMoreText?: string;
  disabled?: boolean;
  fetchSelectedOption?: (id: string) => Promise<SelectOption | null>;
  leftSlot?: React.ReactNode;
  wrap?: boolean;
};

export const InfiniteSelectBase = ({
  value,
  onChange,
  placeholder = "Search and select...",
  useData,
  loadingText = "Loading...",
  noResultsText = "No results found",
  loadingMoreText = "Loading more...",
  disabled = false,
  fetchSelectedOption,
  leftSlot,
  wrap = false,
}: Props) => {
  const [reference, setReference] = useState<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [cachedSelectedOption, setCachedSelectedOption] = useState<SelectOption | null>(null);
  const [isLoadingSelected, setIsLoadingSelected] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const elementsRef = useRef<Array<HTMLElement | null>>([]);
  const labelsRef = useRef<Array<string | null>>([]);

  // Use the custom data hook
  const {
    options,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useData(searchQuery, isOpen);

  // Fetch selected option details if not in loaded options or cache
  useEffect(() => {
    if (!value) {
      setCachedSelectedOption(null);
      setIsLoadingSelected(false);
      return;
    }

    // Check if we already have this option cached
    if (cachedSelectedOption && cachedSelectedOption.id === value) {
      setIsLoadingSelected(false);
      return;
    }

    // Check if value is in currently loaded options
    const found = options.find(opt => opt.id === value);
    if (found) {
      setCachedSelectedOption(found);
      setIsLoadingSelected(false);
      return;
    }

    // If no fetch function provided, we can't load the selected option
    if (!fetchSelectedOption) {
      setIsLoadingSelected(false);
      return;
    }

    // Need to fetch the option details
    setIsLoadingSelected(true);
    fetchSelectedOption(value).then(option => {
      if (option) {
        setCachedSelectedOption(option);
      }
      setIsLoadingSelected(false);
    }).catch(() => {
      setIsLoadingSelected(false);
    });
  }, [value, fetchSelectedOption, options, cachedSelectedOption]); // Re-run when any of these change

  // Get selected option label
  const selectedOption = useMemo(() => {
    if (!value) return null;

    // Use cached option if available and matches current value
    if (cachedSelectedOption && cachedSelectedOption.id === value) {
      return cachedSelectedOption;
    }

    // If still loading, show a loading message
    if (isLoadingSelected) {
      return {
        id: value,
        label: "Loading...",
      };
    }

    // Fallback - this should rarely happen if everything is working correctly
    return {
      id: value,
      label: value, // Show the ID as fallback
    };
  }, [value, cachedSelectedOption, isLoadingSelected]);

  // Handle scroll to load more
  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

    if (scrollPercentage > 0.9 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const handleOpenChange = useCallback((open: boolean) => {
    if (!disabled) {
      setIsOpen(open);
      if (!open) {
        setSearchQuery("");
      }
    }
  }, [disabled]);

  const { refs, floatingStyles, context } = useFloating({
    placement: "bottom-start",
    open: isOpen,
    onOpenChange: handleOpenChange,
    whileElementsMounted: autoUpdate,
    elements: {
      reference,
    },
    middleware: [
      flip({
        padding: 12,
      }),
      offset({ mainAxis: 12 }),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          });
        },
      }),
    ],
  });

  const handleSelect = useCallback(
    (id: string, index: number | null) => {
      // Find the selected option from the current options and cache it
      const selected = options.find(opt => opt.id === id);
      if (selected) {
        setCachedSelectedOption(selected);
      }
      onChange(id, selected);
      setSelectedIndex(index);
      setIsOpen(false);
      setSearchQuery("");
    },
    [onChange, options]
  );

  const handleSearch = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      // Prevent space key from being captured by list navigation when typing
      if (event.key === " ") {
        event.stopPropagation();
      }
    },
    []
  );

  const handleKeyDownCapture = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Backspace" && !searchQuery && value) {
        onChange(undefined, null);
        setCachedSelectedOption(null);
      }
    },
    [onChange, searchQuery, value]
  );

  const listNavigation = useListNavigation(context, {
    listRef: elementsRef,
    activeIndex,
    onNavigate: setActiveIndex,
    focusItemOnHover: false,
  });

  const focus = useFocus(context);
  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [listNavigation, focus, dismiss, click]
  );

  const dropdownContext = useMemo(
    () => ({
      activeIndex,
      selectedIndex,
      getItemProps,
      handleSelect,
    }),
    [activeIndex, selectedIndex, getItemProps, handleSelect]
  );

  return (
    <>
      <Box stretch ref={(el) => setReference(el)}>
        <InputBox
          wrap={wrap}
          leftOnClick={() => !disabled && setIsOpen((prev) => !prev)}
          rightOnClick={() => !disabled && setIsOpen((prev) => !prev)}
          leftSlot={
            leftSlot !== undefined ? leftSlot : (
              selectedOption ? (
                <Value options={[selectedOption]} value={value || ""} />
              ) : undefined
            )
          }
          rightSlot={<Icon size="xs" icon="faChevronDown" />}
        >
          <Input
            tabIndex={0}
            role="combobox"
            placeholder={isEmpty(value) ? placeholder : ""}
            {...getReferenceProps()}
            onKeyDown={handleKeyDown}
            onKeyDownCapture={handleKeyDownCapture}
            ref={refs.setReference}
            onChange={handleSearch}
            value={searchQuery}
            size={1}
            disabled={disabled}
          />
        </InputBox>
      </Box>

      <FloatingPortal>
        <SelectContext.Provider value={dropdownContext}>
          {isOpen && !disabled && (
            <FloatingFocusManager
              context={context}
              modal={false}
              order={["reference", "floating"]}
              returnFocus={false}
            >
              <div
                ref={refs.setFloating}
                className={classes.floating}
                style={floatingStyles}
                {...getFloatingProps()}
              >
                <Card>
                  <div ref={scrollRef} onScroll={handleScroll} style={{ maxHeight: "300px", overflow: "auto" }}>
                    <ScrollBox stretch>
                      <div className={classes.list}>
                        <FloatingList elementsRef={elementsRef} labelsRef={labelsRef}>
                          {isLoading && options.length === 0 ? (
                            <Box padding="12px" align="center" justify="center">
                              <Spinner size={14} />
                              <Text size={12} color="textSecondary">
                                {loadingText}
                              </Text>
                            </Box>
                          ) : (
                            <>
                              {options.map((option) => (
                                <OptionContainer
                                  key={option.id}
                                  {...option}
                                  OptionComponent={Option}
                                />
                              ))}
                              {options.length === 0 && !isLoading && (
                                <Box padding="12px">
                                  <Text color="textSecondary">
                                    {noResultsText}
                                  </Text>
                                </Box>
                              )}
                              {isFetchingNextPage && (
                                <Box padding="8px" align="center" justify="center" gap={8}>
                                  <Spinner size={14} />
                                  <Text size={12} color="textSecondary">
                                    {loadingMoreText}
                                  </Text>
                                </Box>
                              )}
                            </>
                          )}
                        </FloatingList>
                      </div>
                    </ScrollBox>
                  </div>
                </Card>
              </div>
            </FloatingFocusManager>
          )}
        </SelectContext.Provider>
      </FloatingPortal>
    </>
  );
};